/**
 * Thin wrappers injecting web app's `db`/`drizzle`/`userId` into
 * course domain Solid hooks (`@aula/shared/solid`). Components consume
 * these rather than primitives to avoid duplicate wiring.
 */
import type { Accessor } from "solid-js";
import {
  useBlocksQuery,
  useCertificateByIdQuery,
  useCourseCertificateQuery,
  useCourseProgressQuery,
  useCourseQuery,
  useEnrollmentQuery,
  useLeaderboardQuery,
  useLearnerStatsQuery,
  useLessonsQuery,
  useMyAuthoredCoursesQuery,
  useMyEnrollmentsQuery,
  useMyOrganizationsQuery,
  useMyReviewProgressQuery,
  useUnitsQuery,
  useUserLeaderboardOptInQuery,
} from "@aula/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

export function useMyAuthoredCourses() {
  const base = useBase();
  return { query: useMyAuthoredCoursesQuery(base), userId: base.userId };
}

export function useCourseTree(courseId: Accessor<string | undefined>) {
  const base = useBase();
  const scoped = { db: base.db, drizzle: base.drizzle, courseId, staleTime: base.staleTime };
  return {
    course: useCourseQuery(scoped),
    units: useUnitsQuery(scoped),
    lessons: useLessonsQuery(scoped),
    userId: base.userId,
  };
}

export function useLessonBlocks(lessonId: Accessor<string | undefined>) {
  const base = useBase();
  return useBlocksQuery({
    db: base.db,
    drizzle: base.drizzle,
    lessonId,
    staleTime: base.staleTime,
  });
}

export function useEnrollments() {
  const base = useBase();
  return { query: useMyEnrollmentsQuery(base), userId: base.userId };
}

export function useEnrollment(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return useEnrollmentQuery({ ...base, courseId });
}

export function useCourseProgress(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return useCourseProgressQuery({ ...base, courseId });
}

export function useLearnerStats() {
  const base = useBase();
  return useLearnerStatsQuery(base);
}

export function useReviewProgress() {
  const base = useBase();
  return { query: useMyReviewProgressQuery(base), userId: base.userId };
}

export function useLeaderboard(leagueId: Accessor<string>) {
  const base = useBase();
  return useLeaderboardQuery({
    db: base.db,
    drizzle: base.drizzle,
    leagueId,
    staleTime: base.staleTime,
  });
}

export function useMyOrganizations() {
  const base = useBase();
  return useMyOrganizationsQuery({
    db: base.db,
    drizzle: base.drizzle,
    staleTime: base.staleTime,
  });
}

export function useLeaderboardOptIn() {
  const base = useBase();
  return { query: useUserLeaderboardOptInQuery(base), userId: base.userId };
}

/** User's issued certificate (server-side) for a course, if it exists. */
export function useCourseCertificate(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return useCourseCertificateQuery({ ...base, courseId });
}

/** Own certificate by id (for printable view). */
export function useCertificate(certId: Accessor<string | undefined>) {
  const base = useBase();
  return useCertificateByIdQuery({
    db: base.db,
    drizzle: base.drizzle,
    certId,
    staleTime: base.staleTime,
  });
}

export function useCourseWrite() {
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { drizzle, userId };
}
